import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { PerformanceEnrichedRow } from '../../types';
import { formatCurrency } from 'utils';

interface Props {
  rows: PerformanceEnrichedRow[];
}

export const PerformanceKpis = ({ rows }: Props) => {
  const totalDeals = rows.reduce((sum, r) => sum + r.deals, 0);
  const totalFiDeals = rows.reduce((sum, r) => sum + r.fiDeals, 0);
  const fiDealsPct = totalDeals ? Math.round((totalFiDeals / totalDeals) * 100) : 0;
  const avgFinancePen = rows.length
    ? Math.round(rows.reduce((sum, r) => sum + r.financePenetration, 0) / rows.length)
    : 0;
  const totalFiProfit = rows.reduce((sum, r) => sum + r.totalProfit, 0);
  const avgProfitAll = totalDeals ? Math.round(totalFiProfit / totalDeals) : 0;
  const avgProfitFi = totalFiDeals ? Math.round(totalFiProfit / totalFiDeals) : 0;

  const kpis = [
    {
      label: 'Total deals',
      value: totalDeals.toLocaleString(),
      sub: `${totalFiDeals} with F&I revenue (${fiDealsPct}%)`,
    },
    {
      label: 'Finance penetration (avg)',
      value: `${avgFinancePen}%`,
      sub: `${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`,
    },
    {
      label: 'Total F&I profit',
      value: formatCurrency(totalFiProfit, true),
      sub: 'all product categories',
    },
    {
      label: 'Avg profit / deal',
      value: (
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="h5" fontWeight={700} letterSpacing={-0.5}>
            {formatCurrency(avgProfitAll, true)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            all
          </Typography>
        </Stack>
      ),
      sub: `${formatCurrency(avgProfitFi, true)} per F&I deal`,
    },
  ];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
      {kpis.map((kpi, i) => (
        <Card key={i} variant="outlined">
          <CardContent sx={{ p: '16px 18px !important' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              textTransform="uppercase"
              letterSpacing={0.4}
              display="block"
              mb={0.75}
            >
              {kpi.label}
            </Typography>
            {typeof kpi.value === 'string' ? (
              <Typography variant="h5" fontWeight={700} letterSpacing={-0.5}>
                {kpi.value}
              </Typography>
            ) : (
              kpi.value
            )}
            <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
              {kpi.sub}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
